import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Container, Grid, Card, Image, Header, Loader, Dimmer, Label,
    Button, Message, Icon,
} from 'semantic-ui-react';
import moment from 'moment';
import { globalSettings } from './lib/configuration';
import { setSEO, setJSONLD, clearJSONLD } from './lib/seo';
import { useSavedEvents } from './lib/savedEventsStore';
import { t } from './locales';
import { formatMoney } from './lib/currency';
import { withPlatform } from './lib/onlinePlatforms';

const API = globalSettings.PORTAL_API_URL;

const getOrganiserProfile = async (slug) => {
    const res = await fetch(`${API}/api/v1/ticketing/directory/organisers/${slug}/`);
    if (!res.ok) return null;
    return res.json();
};

const getOrganiserEvents = async (slug) => {
    const res = await fetch(`${API}/api/v1/ticketing/directory/events/?organiser=${slug}&sort=date`);
    return res.json();
};

const PURPLE = '#481b52';
const GREEN = '#a4cf58';

function HeartButton({ eventId }) {
    const { isSaved, toggle } = useSavedEvents();
    const saved = isSaved(eventId);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); toggle(eventId); }}
            title={saved ? t('common.removeFromSaved') : t('common.saveEvent')}
            style={{
                position: 'absolute', top: 10, right: 10,
                background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}
        >
            <Icon name={saved ? 'heart' : 'heart outline'}
                style={{ color: saved ? '#e74c3c' : '#888', margin: 0, fontSize: '1rem' }} />
        </button>
    );
}

function DirectoryOrganiser() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const { data: profile, isLoading: profileLoading, isError: profileError } = useQuery({
        queryKey: ['directory-organiser', slug],
        queryFn: () => getOrganiserProfile(slug),
    });

    const { data: eventsData, isLoading: eventsLoading } = useQuery({
        queryKey: ['directory-organiser-events', slug],
        queryFn: () => getOrganiserEvents(slug),
        enabled: !!profile,
    });

    const events = eventsData?.events ?? [];
    const isLoading = profileLoading || eventsLoading;

    useEffect(() => {
        if (!profile) return;
        setSEO({
            title: t('directory.organiserSeoTitle', { organiserName: profile.name }),
            description: profile.description || t('directory.organiserSeoDescriptionFallback', { organiserName: profile.name }),
            image: profile.logo || undefined,
            url: window.location.href,
        });
        setJSONLD({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: profile.name,
            ...(profile.logo ? { logo: profile.logo } : {}),
            ...(profile.description ? { description: profile.description } : {}),
        });
        return () => clearJSONLD();
    }, [profile]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('common.loadingOrganiser')} />
            </Dimmer>
        );
    }

    if (profileError || !profile) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: PURPLE }}>
                <Container textAlign="center">
                    <Icon name="warning sign" size="huge" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <Header as="h2" inverted>{t('directory.organiserNotFound')}</Header>
                    <Button inverted basic onClick={() => navigate('/directory')} style={{ marginTop: 16 }}>
                        {t('directory.backToDirectory')}
                    </Button>
                </Container>
            </div>
        );
    }

    const handleEventClick = (event) => {
        const url = event.instance_id
            ? `/i/${event.instance_slug}/events/${event.id}?instance=${event.instance_id}`
            : `/i/${event.instance_slug}/events/${event.id}`;
        window.location.href = url;
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            {/* Organiser header */}
            <div style={{ backgroundColor: PURPLE, padding: '40px 20px 36px', textAlign: 'center' }}>
                {profile.logo && (
                    <div style={{
                        width: 80, height: 80, borderRadius: 12, overflow: 'hidden',
                        margin: '0 auto 16px', background: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <img src={profile.logo} alt={profile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                )}
                {!profile.logo && (
                    <div style={{
                        width: 72, height: 72, borderRadius: 12, background: 'rgba(255,255,255,0.15)',
                        margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Icon name="building outline" size="big" style={{ color: 'rgba(255,255,255,0.6)', margin: 0 }} />
                    </div>
                )}
                <Header as="h1" inverted style={{ fontSize: 26, fontWeight: 400, marginTop: 0, marginBottom: 8 }}>
                    {profile.name}
                </Header>
                {profile.description && (
                    <p style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 520, margin: '0 auto 16px', lineHeight: 1.6 }}>
                        {profile.description}
                    </p>
                )}
                <Button
                    basic inverted size="small"
                    onClick={() => navigate('/directory')}
                    style={{ marginTop: 4 }}
                >
                    <Icon name="arrow left" />
                    {t('directory.allEventsButton')}
                </Button>
            </div>

            <Container style={{ padding: '24px 0 60px' }}>
                <Header as="h2" style={{ color: PURPLE, marginBottom: '1.25rem' }}>
                    {t('directory.upcomingEvents')}
                    {events.length > 0 && (
                        <Header.Subheader style={{ marginTop: 4, color: '#666' }}>
                            {events.length === 1
                                ? t('directory.eventsListedCountOne')
                                : t('directory.eventsListedCountOther', { count: events.length })}
                        </Header.Subheader>
                    )}
                </Header>

                {events.length === 0 ? (
                    <Message info>
                        <Message.Header>{t('directory.noEventsListed')}</Message.Header>
                        <p>{t('directory.noEventsListedBody')}</p>
                    </Message>
                ) : (
                    <Grid stackable columns={3}>
                        {events.map(event => {
                            const remaining = event.next_instance?.tickets_remaining;
                            const isSoldOut = remaining === 0;
                            const isLowStock = remaining != null && remaining > 0 && remaining <= 10;
                            const isFree = parseFloat(event.ticket_price) === 0;
                            const cardKey = event.instance_id ? `${event.id}-${event.instance_id}` : event.id;

                            return (
                                <Grid.Column key={cardKey}>
                                    <Card
                                        fluid
                                        className="eventCard"
                                        onClick={() => handleEventClick(event)}
                                        style={{
                                            borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                                            opacity: isSoldOut ? 0.7 : 1, position: 'relative',
                                        }}
                                    >
                                        <div style={{ position: 'relative' }}>
                                            {event.image_url ? (
                                                <Image
                                                    src={event.image_url} wrapped ui={false}
                                                    style={{ height: 180, objectFit: 'cover', width: '100%' }}
                                                />
                                            ) : (
                                                <div style={{
                                                    height: 180,
                                                    background: `linear-gradient(135deg, ${PURPLE}, #7b3f8a)`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '3rem' }}>🎟</span>
                                                </div>
                                            )}
                                            <HeartButton eventId={event.id} />
                                            {isSoldOut && (
                                                <div style={{
                                                    position: 'absolute', top: 10, right: 10,
                                                    backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff',
                                                    borderRadius: 6, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700,
                                                }}>{t('common.soldOut')}</div>
                                            )}
                                            {isLowStock && (
                                                <div style={{
                                                    position: 'absolute', top: 10, right: 10,
                                                    backgroundColor: '#e67e22', color: '#fff',
                                                    borderRadius: 6, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700,
                                                }}>{t('common.ticketsLeft', { remaining })}</div>
                                            )}
                                            {event.online_event && (
                                                <div style={{
                                                    position: 'absolute', top: 10, left: 10,
                                                    backgroundColor: 'rgba(52,152,219,0.9)', color: '#fff',
                                                    borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600,
                                                }}>
                                                    <Icon name="wifi" style={{ margin: 0 }} /> {withPlatform(t('common.online'), event.online_platform)}
                                                </div>
                                            )}
                                        </div>

                                        <Card.Content>
                                            <Card.Header style={{ color: isSoldOut ? '#999' : PURPLE }}>
                                                {event.name}
                                            </Card.Header>
                                            {event.next_instance?.date_time && (
                                                <Card.Meta>
                                                    <Icon name="calendar outline" />
                                                    {moment(event.next_instance.date_time).format('ddd D MMM YYYY, HH:mm')}
                                                </Card.Meta>
                                            )}
                                            {event.location && (
                                                <Card.Meta>
                                                    <Icon name="map marker alternate" />
                                                    {event.location}
                                                </Card.Meta>
                                            )}
                                            {event.category && (
                                                <div style={{ marginTop: '0.4rem' }}>
                                                    <Label size="tiny" style={{ backgroundColor: PURPLE, color: '#fff' }}>
                                                        {event.category}
                                                    </Label>
                                                </div>
                                            )}
                                            <Card.Description style={{
                                                marginTop: '0.5rem', overflow: 'hidden',
                                                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                            }}>
                                                {event.description}
                                            </Card.Description>
                                        </Card.Content>

                                        <Card.Content extra style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Label style={{
                                                backgroundColor: isSoldOut ? '#ccc' : isFree ? '#27ae60' : GREEN,
                                                color: '#fff', fontWeight: 700,
                                            }}>
                                                {isSoldOut ? t('common.soldOut') : isFree ? t('common.free') : formatMoney(event.ticket_price, event.currency)}
                                            </Label>
                                            {event.instance_count > 1 && (
                                                <span style={{ fontSize: '0.85rem', color: '#888' }}>
                                                    {t('common.datesCount', { count: event.instance_count })}
                                                </span>
                                            )}
                                            <Button size="small" style={{
                                                backgroundColor: isSoldOut ? '#ccc' : PURPLE,
                                                color: '#fff',
                                            }}>
                                                {isSoldOut ? t('common.view') : t('common.getTickets')}
                                            </Button>
                                        </Card.Content>
                                    </Card>
                                </Grid.Column>
                            );
                        })}
                    </Grid>
                )}
            </Container>
        </div>
    );
}

export default DirectoryOrganiser;
